import { useState, useEffect, useCallback, useRef } from "react";
import { useNetworkStatus } from "./useNetworkStatus";
import { SyncStatus, usePendingQueue } from "./usePendingQueue";
import { useSync } from "store/SyncContext";

/**
 * 应用状态枚举
 */
export enum AppStatus {
    ONLINE_SYNCED = "online_synced",      // 在线 + 已同步（无待同步数据）
    ONLINE_PENDING = "online_pending",    // 在线 + 有待同步数据
    ONLINE_SYNCING = "online_syncing",    // 在线 + 同步中
    ONLINE_FAILED = "online_failed",  // 在线 + 同步失败
    OFFLINE_PENDING = "offline_pending",  // 离线 + 有待同步數據
}

/**
 * 应用状态信息
 */
export interface AppStatusInfo {
    status: AppStatus;
    isOnline: boolean;
    isSyncing: boolean;
    pendingCount: number;
    pendingUserName: string | null;
}

export const useAppStatus = () => {
    const { isOnline } = useNetworkStatus();
    const { getPendingDataInfo, setSyncStatus, syncStatus } = usePendingQueue();
    const { isSyncing } = useSync();
    
    const [appStatus, setAppStatus] = useState<AppStatusInfo>({
        status: AppStatus.ONLINE_SYNCED,
        isOnline: true,
        isSyncing: false,
        pendingCount: 0,
        pendingUserName: null,
    });

    // 刷新应用状态
    const refreshAppStatus = useCallback(async () => {
        const pendingInfo = await getPendingDataInfo();
        
        let status: AppStatus;

        if (isOnline) {
            if (isSyncing) {
                status = AppStatus.ONLINE_SYNCING;
            } else if (pendingInfo.syncStatus === SyncStatus.FAILED || pendingInfo.syncStatus === SyncStatus.PARTIAL_SUCCESS) {
                // 同步失败或部分成功时，显示为失败状态
                status = AppStatus.ONLINE_FAILED;
            } else if (pendingInfo.pendingCount > 0) {
                status = AppStatus.ONLINE_PENDING;
            } else {
                status = AppStatus.ONLINE_SYNCED;
            }
        } else {
            status = AppStatus.OFFLINE_PENDING;
        }

        setAppStatus({
            status,
            isOnline,
            isSyncing,
            pendingCount: pendingInfo.pendingCount,
            pendingUserName: pendingInfo.userName,
        });
    }, [isOnline, isSyncing, getPendingDataInfo]);

    // 监听状态变化 - 只监听基础状态
    useEffect(() => {
        refreshAppStatus();
    }, [isOnline, isSyncing, refreshAppStatus]);
    
    // 单独监听 syncStatus 变化（用 ref 避免无限循环）
    const prevSyncStatusRef = useRef(syncStatus);
    useEffect(() => {
        if (prevSyncStatusRef.current !== syncStatus) {
            prevSyncStatusRef.current = syncStatus;
            refreshAppStatus();
        }
    }, [syncStatus, refreshAppStatus]);

    return {
        ...appStatus,
        refreshAppStatus,
    };
};

